import Debug from 'debug';
import { InteractionStateStoreModule } from '../../../store'
import { InteractionStateMachineData } from '../interaction-state-machine-data'
import { waitToLoad, CancelToken, cancel } from '@/app/shared/utils'
import { InteractionStateService } from '../../interaction-state-service';
import { InteractionStateChooserFactory } from '@/app/interaction/api';
import { reportNonFatalError } from '@/app/error-handling';

const debug = Debug('interaction-waiting-for-remote-state')

type ChangeTracking<T> = {
  changed: true
  value: T
} | { changed: false }

export class WaitingForRemoteState<TState> {

  public readonly name = 'waiting-for-remote-state'

  private hasInitialLocalState: boolean = false
  private initialLocalState: TState

  private localStateChange: ChangeTracking<TState> = { changed: false }
  private localResultChange: ChangeTracking<{ progress: number, status: 'incomplete' | 'completed' }> =
        { changed: false }

  stateEvents: {
    onResetComponent: (state: TState | null) => void
    onLoaded: () => void
  }

  private cancelToken: CancelToken = { cancel: false }

  constructor(
    private stateStore: InteractionStateStoreModule,
    private stateService: InteractionStateService,
    private data: InteractionStateMachineData,
    private stateChooserFactory: InteractionStateChooserFactory<TState>) {
  }

  enter() {

    debug('enter', this.data.id, this.data.type)

    let state: TState | null = null
    const localState = this.stateStore.findLocalState<TState>(this.data.id)
    debug('local state', this.data.id, this.data.type)
    if (localState.success) {
      this.hasInitialLocalState = true
      state = localState.value.state
      this.initialLocalState = state
    }

    this.waitAndLoadRemoteState().catch(err => {
      debug('WaitForRemoteState error', err)
      reportNonFatalError(err)
    })

    this.stateEvents.onResetComponent(state)
  }

  cancel() {
    debug('cancel', this.data.id, this.data.type)
    
    cancel(this.cancelToken)
  }

  exit() {
    debug('exit', this.data.id, this.data.type)
  }

  updateInteractionState(state: TState) {
    this.localStateChange = {
      changed: true,
      value: state
    }

    // do not save into tracking while loading
    this.stateService.updateInteractionState({
      id: this.data.id,
      type: this.data.type,
      state: state
    }, { saveInTracking: false })
  }

  updateInteractionResult(progress: number, status: 'incomplete' | 'completed') {

    this.localResultChange = {
      changed: true,
      value: { progress, status }
    }

    // do not save into tracking while loading
    this.stateService.updateInteractionResult({
      id: this.data.id,
      type: this.data.type,
      progress,
      status
    }, { saveInTracking: false })
  }

  private async waitAndLoadRemoteState() {
    debug('wait', this.data.id, this.data.type)
    const waitResult = await waitToLoad(() => this.stateStore.remoteStatePhase === 'loaded', this.cancelToken)

    debug('wait finished', this.data.id, this.data.type, 'success: ', waitResult.success)
    if (!waitResult.success) {
      return
    }

    const remoteStateResult = this.stateStore.findRemoteState<TState>(this.data.id)
    debug('remote state result', remoteStateResult)

    if (!remoteStateResult.success) {
      // we have no remote state
      this.propagateLocalChangesToTracking()
      this.stateEvents.onLoaded()
      return
    }

    let useRemote
    if (!this.hasInitialLocalState && !this.localStateChange.changed) {
      debug('there is no local fill in, reset state from remote data', this.data.id, this.data.type)
      useRemote = true
    } else {
      const latestLocalState = this.latestLocalState()
      
      const chooser = this.stateChooserFactory(this.data.type)
      const chooseResult = chooser.choose(latestLocalState, remoteStateResult.value.state)

      debug('interaction already filled, choosed based on chooser', this.data.id, this.data.type, chooseResult)

      useRemote = chooseResult === 'use-remote'
    }

    if (useRemote) {
      debug('loading the remote state', remoteStateResult.value.state)

      this.stateEvents.onResetComponent(remoteStateResult.value.state)
    } else {
      // use the local, propagate local changes to tracking if we have
      this.propagateLocalChangesToTracking()
    }
      
    this.stateEvents.onLoaded()
  }

  private latestLocalState() {
    if (this.localStateChange.changed) {
      return this.localStateChange.value
    }

    return this.initialLocalState
  }

  private propagateLocalChangesToTracking() {

    debug('propagate-local-changes-to-tracking', this.localResultChange, this.localStateChange)
    
    if (this.localResultChange.changed) {
      this.stateService.updateInteractionResult({
        id: this.data.id,
        type: this.data.type,
        progress: this.localResultChange.value.progress,
        status: this.localResultChange.value.status
      }, { saveInTracking: true })
    }

    if (this.localStateChange.changed) {
      this.stateService.updateInteractionState({
        id: this.data.id,
        type: this.data.type,
        state: this.localStateChange.value
      }, { saveInTracking: true })
    }
  }
}
